<template>
  <Sheet>
    <div
      class="mt-2.5 w-full shrink-0 px-5 pt-[5.5px] pb-[3.5px] text-gray-800"
    >
      <div class="flex items-start">
        <div class="mr-4 size-9 shrink-0">
          <Thumbnail :type="thumbType" :src="thumbnailUrl" />
        </div>
        <div v-truncate class="w-full">
          <div
            class="mb-2 font-medium text-[17px] leading-[20px]"
            data-truncate-target
          >
            {{ file.name }}
          </div>
          <div
            class="flex items-center font-secondary text-[14px] leading-[18px] text-gray-500"
          >
            <template v-if="isInactive">—</template>
            <template v-else-if="firstTimestamp">
              <DacrMarkIcon class="mr-1.5 size-[14px]" />
              <div>{{ formatTimestamp(firstTimestamp) }}</div>
              <div
                v-if="hasMoreRegistrations"
                class="ml-2 flex cursor-pointer items-center gap-x-1.5 font-medium"
                @click="handleTimestampClick"
              >
                <div>+{{ remainingRegistrations }} more</div>
                <ChevronDownIcon class="size-[14px]" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-[12.5px] h-px w-full shrink-0 bg-gray-800/10" />

    <Sheet.Body class="px-0!">
      <template v-if="isOwner && originFlagEnabled && billingStore.isPro">
        <Sheet.MenuItem
          :icon="GlobeAddIcon"
          :disabled="isInactive"
          @click="handleRegisterCountries"
        >
          Register in more countries
          <template #description>
            Recognized and registered under local laws
          </template>
        </Sheet.MenuItem>

        <Sheet.MenuSeparator />
      </template>

      <Sheet.MenuItem
        :icon="ShareIcon"
        :disabled="isInactive"
        @click="handleShare"
      >
        Share
      </Sheet.MenuItem>
      <Sheet.MenuItem
        :icon="SendIcon"
        :disabled="isInactive"
        @click="handleInvite"
      >
        Invite
      </Sheet.MenuItem>
      <Sheet.MenuItem
        v-if="canDownload"
        :icon="DownloadIcon"
        @click="handleDownload"
      >
        Download
      </Sheet.MenuItem>

      <Sheet.MenuSeparator />

      <Sheet.MenuItem :icon="GearIcon" @click="handleSettings">
        Copyright settings
      </Sheet.MenuItem>
      <Sheet.MenuItem :icon="LockIcon" @click="handlePrivacy">
        Privacy controls
      </Sheet.MenuItem>
      <Sheet.MenuItem :icon="MoveIcon" @click="handleMove">
        Move
      </Sheet.MenuItem>
      <Sheet.MenuItem
        :icon="ArchivedIcon"
        class="text-red-600"
        @click="handleArchive"
      >
        Archive
      </Sheet.MenuItem>

      <Sheet.MenuSeparator />

      <Sheet.MenuItem :icon="InfoIcon" @click="handleFileInfoClick">
        Copyright info
      </Sheet.MenuItem>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Sheet, Thumbnail, useSheet } from '@dacr/library'
import {
  ShareIcon,
  SendIcon,
  DownloadIcon,
  GearIcon,
  LockIcon,
  MoveIcon,
  ArchivedIcon,
  InfoIcon,
  DacrMarkIcon,
  ChevronDownIcon,
  GlobeAddIcon,
} from '@dacr/icons'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { useBillingStore } from '@/features/billing/store'
import {
  CopyrightStatus,
  FilePermission,
  FileRole,
  type File,
} from '@/shared/graphql/router/graphql'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'

const { close } = useSheet()

const props = defineProps<{
  file: File
}>()

const file = computed(() => props.file)

const isInactive = computed(
  () => file.value.copyright?.status === CopyrightStatus.Inactive
)

const isOwner = computed(() => file.value.role === FileRole.Owner)

const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const billingStore = useBillingStore()

const canDownload = computed(() =>
  file.value.permissions.includes(FilePermission.Download)
)

const firstTimestamp = computed(
  () => file.value.copyright?.registrations?.[0]?.timestamp
)

const registrations = computed(() => file.value.copyright?.registrations ?? [])

const hasMoreRegistrations = computed(() => registrations.value.length > 1)

const remainingRegistrations = computed(() => registrations.value.length - 1)

const thumbnailUrl = computed(() =>
  applyAuthorizationToken(file.value.asset?.previewImageUrl)
)

const thumbType = computed(() => fileTypeToThumbnail(file.value.type))

const handleShare = () => {
  close()
  openDialog(Dialog.Share, { props: { file: file.value } })
}

const handleInvite = () => {
  close()
  openDialog(Dialog.FileAccess, { props: { file: file.value } })
}

const handleDownload = () => {
  close()
  openModal(Modals.Download, { props: { file: file.value } })
}

const handleSettings = () => {
  close()
  openDialog(Dialog.FileSettings, { props: { file: file.value } })
}

const handleRegisterCountries = () => {
  close()
  openDialog(Dialog.RegisterCountries, { props: { files: [file.value] } })
}

const handlePrivacy = () => {
  close()
  openDialog(Dialog.FilePrivacySettings, { props: { file: file.value } })
}

const handleMove = () => {
  close()
  openDialog(Dialog.MoveItems, { props: { ids: [file.value.id] } })
}

const handleArchive = () => {
  close()
  openModal(Modals.ArchiveFiles, { props: { files: [file.value] } })
}

const handleTimestampClick = () => {
  close()
  openDialog(Dialog.Registrations, { props: { file: file.value } })
}

const handleFileInfoClick = () => {
  close()
  openDialog(Dialog.CopyrightInfo, { props: { file: file.value } })
}
</script>
